import { Workspace } from "@/backend/models/workspace";
import { create } from "zustand";
import { WorkspaceController } from "../controllers/workspaceController";
import { immer } from "zustand/middleware/immer";
import { WorkspaceCreateRequest, WorkspaceResponse } from "@/types/workspaceTypes";
import { WorkspaceParams } from "@/backend/access/workspace/types";

type WorkspaceStore = {
  workspaces: Workspace[];
  setWorkspaces: (workspaces: WorkspaceParams[]) => void;
  fetchWorkspacesByPropelOrgWithAssigned: () => Promise<void>;
  fetchWorkspacesByEnterpriseForAdmin: (enterpriseId: string) => Promise<void>;
  reset: () => void;
  createWorkspace: (data: WorkspaceCreateRequest) => Promise<WorkspaceResponse>;
  updateWorkspace: (data: WorkspaceParams) => Promise<WorkspaceResponse>;
  updateExecutorEnterpriseId: (workspaceId: string, executorEnterpriseId: string | undefined) => Promise<void>;
  deleteWorkspace: (id: string) => Promise<void>;
  refetchWorkspace: (id: string) => Promise<void>;
  fetchAllWorkspacesForAdmin: () => Promise<void>;
};

const initialState = {
  workspaces: [],
};

export const useWorkspaceStore = create<WorkspaceStore>()(immer((set, get) => ({
  ...initialState,
  reset: () => {
    set(initialState);
  },
  setWorkspaces: (workspaces) => {
    set({ workspaces: workspaces.map((workspace) => new Workspace(workspace)) });
  },
  fetchWorkspacesByPropelOrgWithAssigned: async () => {
    const response = await new WorkspaceController().readAllByPropelOrgWithAssigned();
    if (!response.errors && response.workspaces) {
      const workspaces = get().workspaces;
      const allWorkspaces = [...workspaces, ...response.workspaces];
      const uniqueWorkspaces = new Map(allWorkspaces.map(workspace => [workspace.id, workspace]));
      get().setWorkspaces(Array.from(uniqueWorkspaces.values()));
    }
  },
  fetchWorkspacesByEnterpriseForAdmin: async (enterpriseId: string) => {
    const response = await new WorkspaceController().readAllByEnterpriseForAdmin(enterpriseId);
    if (!response.errors && response.workspaces) {
      get().setWorkspaces(response.workspaces.map((workspace) => new Workspace(workspace)));
    }
  },
  createWorkspace: async (data: WorkspaceCreateRequest) => {
    const response = await new WorkspaceController().create(data);
    if (!response.errors && response.workspace) {
      const workspaces = get().workspaces;
      get().setWorkspaces([...workspaces, response.workspace]);
    }
    return response;
  },
  updateWorkspace: async (data: WorkspaceParams) => {
    const response = await new WorkspaceController().update(data);
    if (!response.errors && response.workspace) {
      const workspaces = get().workspaces;
      const { setWorkspaces } = get();
      const updatedWorkspaces = workspaces.map(
        (workspace) => {
          if (workspace.id === response.workspace?.id) {
            return new Workspace(response.workspace!);
          }
          return workspace;
        }
      );
      setWorkspaces(updatedWorkspaces);
    }
    return response;
  },
  updateExecutorEnterpriseId: async (workspaceId, executorEnterpriseId) => {
    const response = await new WorkspaceController().updateExecutorEnterpriseId(workspaceId, executorEnterpriseId);
    const { workspace, errors } = response;
    if (!errors && workspace) {
      const workspaces = get().workspaces;
      get().setWorkspaces(workspaces.map((w) => w.id === workspaceId ? workspace : w));
    }
  },
  deleteWorkspace: async (id: string) => {
    const response = await new WorkspaceController().delete(id);
    if (!response.errors) {
      const workspaces = get().workspaces;
      get().setWorkspaces(workspaces.filter((workspace) => workspace.id !== id));
    }
  },
  refetchWorkspace: async (id: string) => {
    const response = await new WorkspaceController().read(id);
    if (!response.errors && response.workspace) {
      const workspaces = get().workspaces;
      get().setWorkspaces(workspaces.map((w) => w.id === id ? response.workspace! : w));
    }
  },
  fetchAllWorkspacesForAdmin: async () => {
    const response = await new WorkspaceController().readAllForAdmin();
    if (!response.errors && response.workspaces) {
      get().setWorkspaces(response.workspaces.map((workspace) => new Workspace(workspace)));
    }
  },
})));
